import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DLG = "bg-[#121722] border-[#242F42] text-white max-h-[90vh] overflow-y-auto";

export function ReviewDialog({ action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [payoutRef, setPayoutRef] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setNote("");
    setPayoutRef("");
  }, [action]);
  if (!action) return null;
  const approve = action.kind === "approve";
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(action.url, { note: note || null, payout_ref: payoutRef || null });
      toast.success(approve ? "Approved" : "Rejected");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DLG} data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-display">{approve ? "Approve" : "Reject"} {action.label}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {action.summary && <div className="rounded-lg border border-[#1E2738] bg-[#0B0E14] p-3 text-sm text-slate-300">{action.summary}</div>}
          {approve && action.payout && (
            <div><label className="nx-label">Payout reference (optional)</label>
              <input className="nx-input num" value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} data-testid="review-payout-ref-input" /></div>
          )}
          <div><label className="nx-label">Note (optional)</label>
            <input className="nx-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid="review-note-input" /></div>
          <button onClick={submit} disabled={busy} className={`${approve ? "nx-btn-primary" : "nx-btn-danger"} w-full py-3`} data-testid="review-confirm-btn">
            {busy && <Loader2 size={16} className="animate-spin" />}Confirm {approve ? "approval" : "rejection"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const RuleSelect = ({ value, onChange, testid }) => (
  <select className="nx-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    <option value="percent">Percentage (%)</option>
    <option value="flat">Flat (USD)</option>
  </select>
);

const EMPTY = { referral_code: "", channel_name: "", first_deposit_bonus: 5, deposit_type: "percent", deposit_value: 5, trading_type: "percent", trading_value: 2, active: true };

export function PartnerDialog({ target, onClose, onDone }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) setF(target.is_youtuber && target.partner ? { ...EMPTY, ...target.partner, referral_code: target.referral_code } : { ...EMPTY, referral_code: "" });
  }, [target]);
  if (!target) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/youtubers/${target.id}`, {
        ...f, first_deposit_bonus: Number(f.first_deposit_bonus), deposit_value: Number(f.deposit_value), trading_value: Number(f.trading_value),
      });
      toast.success(target.is_youtuber ? "Partner updated" : "User promoted to YouTuber Partner");
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DLG} data-testid="partner-dialog">
        <DialogHeader><DialogTitle className="font-display">{target.is_youtuber ? "Edit" : "Promote"} YouTuber Partner</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">{target.name} · {target.email} · <span className="num">{target.user_code}</span></p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="nx-label">Unique referral code</label><input required className="nx-input num uppercase" value={f.referral_code} onChange={set("referral_code")} placeholder="e.g. TECHGURU" data-testid="partner-code-input" /></div>
            <div><label className="nx-label">Channel name</label><input className="nx-input" value={f.channel_name || ""} onChange={set("channel_name")} data-testid="partner-channel-input" /></div>
          </div>
          <div><label className="nx-label">Fixed first-deposit bonus (USD)</label><input type="number" step="0.01" min="0" className="nx-input num" value={f.first_deposit_bonus} onChange={set("first_deposit_bonus")} data-testid="partner-fdb-input" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="nx-label">Later-deposit commission</label><RuleSelect value={f.deposit_type} onChange={set("deposit_type")} testid="partner-deposit-type" /></div>
            <div><label className="nx-label">Value</label><input type="number" step="0.01" min="0" className="nx-input num" value={f.deposit_value} onChange={set("deposit_value")} data-testid="partner-deposit-value" /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="nx-label">Trading commission</label><RuleSelect value={f.trading_type} onChange={set("trading_type")} testid="partner-trading-type" /></div>
            <div><label className="nx-label">Value</label><input type="number" step="0.01" min="0" className="nx-input num" value={f.trading_value} onChange={set("trading_value")} data-testid="partner-trading-value" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-[#10B981]" data-testid="partner-active-checkbox" /> Partner commissions active
          </label>
          <p className="text-[11px] text-slate-500">Flat trading commission is paid per daily-return event; percentage is applied to each return amount. The first approved deposit earns only the fixed bonus; later deposits earn the deposit commission.</p>
          <button className="nx-btn-primary w-full py-3" disabled={busy} data-testid="partner-save-btn">{busy && <Loader2 size={16} className="animate-spin" />}Save partner</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const ActionBtns = ({ onApprove, onReject, id }) => (
  <div className="flex justify-end gap-2">
    <button onClick={onApprove} className="rounded-md border border-[#10B981]/40 bg-[#10B981]/10 px-3 py-1 text-xs font-medium text-[#10B981] hover:bg-[#10B981]/20" data-testid={`approve-btn-${id}`}>Approve</button>
    <button onClick={onReject} className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-400 hover:bg-red-500/20" data-testid={`reject-btn-${id}`}>Reject</button>
  </div>
);
